import React from 'react';
import socket from '.';
import {
  Topics,
  ChatEvents,
  IJoinChatGroupData,
  IUnreadConversationsData,
  IChatData,
  IMessageItem,
  IMessageAddData,
  IUploadFile,
} from './config';
import * as ChatServices from 'services/api/chat';
import { useQuery, useMutation } from 'react-query';
import { getSessionId } from 'utils/session';
import store from 'redux/store';
import { uploadFile } from 'services/api/file';
import EntityType from 'constants/entity';

// https://stackoverflow.com/questions/23092624/socket-io-removing-specific-listener

interface IChatsOptions {
  userId?: string;
  productId?: number;
  pageSize: number;
}

export const useChats = ({ userId, pageSize, productId }: IChatsOptions) => {
  const { refetch, data } = useQuery(['contacts', userId, pageSize], () =>
    ChatServices.fetchChats({
      userId,
      paging: { pageSize },
      productId,
    })
  );

  // console.log('hey', refetch, data);
  React.useEffect(() => {
    const event = Topics.MemberChats;
    const action = (data: any) => {
      // check if the message is from the true room
      console.log('useChats');
      refetch();
    };
    socket.on(event, action);

    return () => {
      socket.removeListener(event, action);
    };
  }, []);

  return data?.data;
};

export const useChatError = () => {
  const [error, setError] = React.useState();

  React.useEffect(() => {
    const event = 'error';
    const action = (error: any) => {
      console.log('Error::', error);
      setError(error);
    };
    socket.on(event, action);

    return () => {
      socket.removeListener(event, action);
    };
  }, [setError]);

  return error;
};

export const useChatConnect = () => {
  const [connect, setConnect] = React.useState<boolean>(false);

  React.useEffect(() => {
    const event = 'connect';
    const action = () => {
      console.log('Connected!');
      setConnect(true);
    };
    socket.on(event, action);

    return () => {
      socket.removeListener(event, action);
    };
  }, [setConnect]);

  return connect;
};

interface IConversationOptions {
  pageSize: number;
}

export const useConversation = (
  chatGroupGuid: string | null,
  options?: IConversationOptions
) => {
  const [conversation, setConversation] =
    React.useState<ChatServices.IConversationData>();
  const [socketMessage, setSocketMessage] = React.useState(false);

  useQuery(
    [`conversation`, chatGroupGuid, options?.pageSize],
    () => {
      if (chatGroupGuid) {
        const sessionId = getSessionId();
        return ChatServices.fetchChatConversation(
          chatGroupGuid,
          sessionId as string,
          {
            pageSize: options?.pageSize ?? 10,
          }
        );
      }
      return null;
    },
    {
      onSuccess: (data) => {
        if (data) {
          setConversation(data.data);
          setSocketMessage(false);
        }
      },
    }
  );

  React.useEffect(() => {
    socket.emit(ChatEvents.JoinChatGroup, {
      chatGroupGuid,
    } as IJoinChatGroupData);
  }, [chatGroupGuid]);

  React.useEffect(() => {
    const event = Topics.MessageAdd(chatGroupGuid as string);
    const action = (data: IMessageItem) => {
      // check if the message is from the true room
      console.log('useChatMessages', data);
      setSocketMessage(true);
      setConversation((conversation) =>
        conversation
          ? {
              ...conversation,
              items: [data, ...(conversation?.items.slice(0, 90) ?? [])],
              paging: {
                ...conversation.paging,
                total: conversation.paging.total + 1,
              },
            }
          : undefined
      );
    };

    socket.on(event, action);
    return () => {
      socket.removeListener(event, action);
    };
  }, [setConversation, chatGroupGuid, setSocketMessage]);

  return { conversation, socketMessage };
};

export const useChatgroupDetails = (chatgroupGuid: string | null) => {
  return useQuery(['chatgroupDetails', chatgroupGuid], () =>
    chatgroupGuid ? ChatServices.fetchChatgroupDetails(chatgroupGuid) : null
  );
};

export const useChatgroupIsActive = (chatgroupGuid: string | null) => {
  return null;
};

type ChatInitialData = ChatServices.IChatsData['items'][0];

export const useChatData = (
  chatGroupGuid: string | null,
  initialData: ChatInitialData
  // memberId: string
) => {
  const [data, setData] = React.useState<ChatInitialData>(initialData);

  React.useEffect(() => {
    socket.emit(ChatEvents.JoinChatGroup, {
      chatGroupGuid,
    } as IJoinChatGroupData);
  }, [chatGroupGuid]);

  React.useEffect(() => {
    const event = Topics.Chat(chatGroupGuid as string);

    const action = ({ chatMembers, ..._data }: IChatData) => {
      // console.log('Chat data!!~~~~~', _data, chatGroupGuid);
      const socketUserId = store.getState().AuthReducer.login
        ? store.getState().AuthReducer.data.guid
        : getSessionId();
      if (_data.guid === chatGroupGuid) {
        setData({
          ...initialData,
          ..._data,
          read:
            (chatMembers
              ? Boolean(
                  chatMembers.find((el) => el.guid === socketUserId)?.read
                )
              : _data.read) ?? false,
        });
      }
    };

    socket.on(event, action);
    return () => {
      socket.removeListener(event, action);
    };
  }, [setData, chatGroupGuid, initialData]);

  return data;
};

export const useChatsNotifications = (ignorePaths: string[] = []) => {
  const [notification, setNotification] =
    React.useState<IMessageAddData | null>(null);

  React.useEffect(() => {
    const event = Topics.ChatNotifications;
    const action = (data: IMessageAddData) => {
      let flag = false;
      ignorePaths.forEach((el) => {
        if (window.location.href.includes(el)) {
          flag = true;
          return;
        }
      });

      if (flag) {
        return;
      }

      // check if the message is from the true room
      console.log('chatsNotification');
      setNotification(data);
    };
    socket.on(event, action);

    return () => {
      socket.removeListener(event, action);
    };
  }, [setNotification]);

  return notification;
};

export const useUnreadConversations = (ignorePaths: string[] = []) => {
  const [count, setCount] = React.useState<number>(0);
  useQuery(
    ['unread-conversations'],
    () => ChatServices.fetchUnreadConversations(),
    {
      onSuccess: (data) => {
        setCount(data.data.count);
      },
    }
  );

  React.useEffect(() => {
    const event = Topics.UnreadChats;
    const action = (data: IUnreadConversationsData) => {
      let flag = false;
      ignorePaths.forEach((el) => {
        if (window.location.href.includes(el)) {
          flag = true;
          return;
        }
      });

      if (flag) {
        return;
      }

      // check if the message is from the true room
      console.log('useUnreadConversations');
      setCount(data.count);
    };
    socket.on(event, action);

    return () => {
      socket.removeListener(event, action);
    };
  }, [setCount]);

  return count;
};

export const useUploadFile = (payload: IUploadFile) => {
  return useMutation(
    (payload: { entityType: EntityType; file: File }) =>
      uploadFile(payload.file, payload.entityType),
    {
      onSuccess: (response) => {
        console.log('Response:::', response);
        payload.onSuccess(response);
      },
    }
  );
};
